import { Col, Row } from "reactstrap";
import Card from "@components/ui-components/card";
import CommonModal from "@components/ui-components/modal";
import FormView from "@components/views/form-view";
import { ComponentSpinner } from "@components/spinner";
import InputField from "@components/fields/input-field";
import LongNameFormatter from "@components/formatters/long-name-formatter";
import Paginate from "@components/ui-components/paginate";
import { ROW_PER_PAGINATION_OPTIONS } from "@src/utility/constants";
import capitalize from "lodash/capitalize";
import classNames from "classnames";
import size from "lodash/size";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import useUpsert from "./use-upsert";
import LoadingButton from "@components/ui-components/loading-button";

const UpsertNotes = (props) => {
    const {
        modalConfig: { isOpen, row },
        handleModal,
        macros
    } = props;

    const isEdit = !!size(row);
    
    const [modalState, setModalState] = useState({
        attribute: false,
        template : false
    });
    const {
        handleMacroSelect,
        loading,
        onSubmit,
        isDirty,
        methods,
        selectedMacros,
        templates: { data, pagination },
        templateLoading,
        handleTemplateSelect,
        handlePagination,
        selectedTemplateId
    } = useUpsert({
        ...props,
        isEdit
    });

    const {
        control,
        formState: { errors }
    } = methods;
    const { t } = useTranslation();

    const toggleModal = (type) => {
        setModalState((prev) => ({
            ...prev,
            [type]: !prev[type]
        }));
    };
    const content = [
        {
            leftChild: {
                title: "Note Details",
                desc : "Enter the basic details of the note including title and description"
            },
            rightChild: (
                <Row>
                    <InputField
                        label="Title"
                        id="title"
                        name="title"
                        required
                        control={control}
                        errors={errors}/>
                    <InputField
                        inputClassName="notes-text-area"
                        rows={5}
                        type="textarea"
                        label="Description"
                        id="desc"
                        name="desc"
                        required
                        control={control}
                        errors={errors}/>
                </Row>
            )
        },
        {
            leftChild: {
                title: "Quick Actions",
                desc : "Add dynamic attributes or load from existing templates"
            },
            rightChild: (
                <Row className="mt-1">
                    <Col lg={6} className="mb-1">
                        <LoadingButton
                            testId="add-attributes"
                            color="primary"
                            outline
                            className="w-100 py-1 d-flex align-items-center justify-content-center"
                            size="lg"
                            onClick={() => toggleModal("attribute")}
                            text="Add Attributes"
                            icon="plus"/>
                    </Col>
                    <Col lg={6} className="mb-1">
                        <LoadingButton
                            testId="load-templates"
                            color="info"
                            outline
                            className="w-100 py-1 d-flex align-items-center justify-content-center"
                            size="lg"
                            onClick={() => toggleModal("template")}
                            text="Load from Templates"
                            icon="plus"/>
                    </Col>
                </Row>
            )
        }
    ];

    return (
        <>
            {/* Add Notes Modal */}
            <CommonModal
                type="large"
                isOpen={isOpen}
                handleModal={handleModal}
                title={`${!isEdit ? "Add" : "Edit"} Note`}
                loading={loading}
                confirmButton={{
                    txt    : isEdit ? "Update" : "Add",
                    icon   : "content-save",
                    onClick: onSubmit,
                    loading
                }}
                isDirty={isDirty}
                body={<FormView content={content} />}/>

            {/* Add Attributes Modal */}
            <CommonModal
                type="large"
                isOpen={modalState.attribute}
                handleModal={() => toggleModal("attribute")}
                title="Add Dynamic Attributes"
                confirmButton={{
                    txt    : "Done",
                    onClick: () => toggleModal("attribute")
                }}
                body={
                    <Card>
                        <div>
                            <h5 className="mt-1">{t("Select Attributes to Add")}</h5>
                            <p className="text-muted small mb-1">
                                {t(
                                    "Click on any attribute to add it to your note. Selected attributes will be highlighted."
                                )}
                            </p>
                            <div
                                className="border rounded p-1 overflow-y-auto"
                                style={{ maxHeight: "400px" }}>
                                {Object.entries(macros).map(([categoryKey, categoryValue]) => (
                                    <div key={categoryKey}>
                                        <Row>
                                            {Object.entries(categoryValue).map(
                                                ([itemKey, itemValue]) => (
                                                    <Col lg={6} xl={4} key={itemKey} className="mb-1">
                                                        <div
                                                            onClick={() => handleMacroSelect(itemValue, itemKey)
                                                            }
                                                            className={classNames(
                                                                "cursor-pointer py-1 border rounded text-center transition-all",
                                                                "hover-bg-primary font-medium-2",
                                                                {
                                                                    "bg-light-primary border-primary text-primary":
                                    selectedMacros.includes(
                                        `${itemKey.toUpperCase()}: ${itemValue}`
                                    ),
                                                                    "bg-light border-light":
                                    !selectedMacros.includes(
                                        `${itemKey.toUpperCase()}: ${itemValue}`
                                    )
                                                                }
                                                            )}>
                                                            <div className="fw-bold">
                                                                {capitalize(itemKey)}
                                                            </div>
                                                            <small className="text-muted">{itemValue}</small>
                                                        </div>
                                                    </Col>
                                                )
                                            )}
                                        </Row>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Card>
                }/>

            {/* Load Templates Modal */}
            <CommonModal
                type="large"
                isOpen={modalState.template}
                handleModal={() => toggleModal("template")}
                title="Load from Templates"
                confirmButton={{
                    txt    : "Done",
                    onClick: () => toggleModal("template")
                }}
                body={
                    <Card>
                        <div>
                            {templateLoading ? (
                                <div className="text-center py-5">
                                    <ComponentSpinner />
                                </div>
                            ) : (
                                <>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <h5>
                                            {t("Available Templates")} ({size(data)})
                                        </h5>
                                    </div>
                                    <p className="text-muted small mb-1">
                                        {t("Select a template to load its content into your note.")}
                                    </p>

                                    <div
                                        className="rounded overflow-y-auto"
                                        style={{ maxHeight: "400px" }}>
                                        {size(data) === 0 ? (
                                            <div className="text-center py-5">
                                                <p className="text-muted">
                                                    {" "}
                                                    {t("No templates available.")}
                                                </p>
                                            </div>
                                        ) : (
                                            <Row>
                                                {data.map((item) => (
                                                    <Col lg={12} key={item._id} className="mb-1">
                                                        <div
                                                            className={classNames(
                                                                "border rounded p-1 cursor-pointer transition-all",
                                                                "hover-bg-primary",
                                                                {
                                                                    "bg-light-primary border-primary":
                                    selectedTemplateId === item._id,
                                                                    "bg-light border-light":
                                    selectedTemplateId !== item._id
                                                                }
                                                            )}
                                                            onClick={() => handleTemplateSelect(item)}>
                                                            <div className="d-flex justify-content-between align-items-start">
                                                                <div className="flex-grow-1">
                                                                    <h6 className="">{item.title}</h6>
                                                                    <LongNameFormatter
                                                                        max={120}
                                                                        content={item.content}
                                                                        className="text-muted small"/>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                ))}
                                            </Row>
                                        )}

                                        {size(data) > 0 && (
                                            <div>
                                                <Paginate
                                                    paginationRowsPerPageOptions={ROW_PER_PAGINATION_OPTIONS}
                                                    handlePageChange={(page) => handlePagination({ page, limit: pagination?.limit || 10 })}
                                                    currentPage={pagination?.page}
                                                    handleLimit={(limit) => handlePagination({ limit, page: 1 })}
                                                    totalRecords={pagination?.totalRecords}
                                                    {...pagination}/>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </Card>
                }/>
        </>
    );
};

export default UpsertNotes;
